import {CommonModule} from '@angular/common';
import {Component, OnInit, inject} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {HttpErrorResponse} from '@angular/common/http';
import {UiIconComponent, UiSelectComponent, UiToastService, UiToggleComponent} from 'freyr-ui';
import {AdminApiService} from '../../admin-api.service';
import {AdminManagedScheduler, AdminManagedSchedulerFrequency, AdminManagedSchedulerUpdateRequest} from '../../admin.models';
import {AdminI18nService} from '../../core/i18n/admin-i18n.service';
import {AdminTranslatePipe} from '../../core/i18n/admin-translate.pipe';

type MonthlyPattern = 'DAY_OF_MONTH' | 'NTH_WEEKDAY' | 'LAST_DAY';
type YearlyPattern = 'DATE' | 'NTH_WEEKDAY';

interface SchedulerDraft {
  enabled: boolean;
  frequency: AdminManagedSchedulerFrequency;
  intervalValue: number;
  dayOfWeek: string | null;
  weekdayOccurrence: number | null;
  dayOfMonth: number | null;
  monthOfYear: number | null;
  lastDayOfMonth: boolean;
  timeOfDay: string | null;
  monthlyPattern: MonthlyPattern;
  yearlyPattern: YearlyPattern;
}

@Component({
  selector: 'app-schedulers-page',
  standalone: true,
  imports: [CommonModule, FormsModule, UiIconComponent, UiSelectComponent, UiToggleComponent, AdminTranslatePipe],
  templateUrl: './schedulers-page.component.html',
  styleUrl: './schedulers-page.component.scss'
})
export class SchedulersPageComponent implements OnInit {
  private readonly api = inject(AdminApiService);
  private readonly i18n = inject(AdminI18nService);
  private readonly toast = inject(UiToastService);

  readonly frequencies: AdminManagedSchedulerFrequency[] = ['SECONDLY', 'MINUTELY', 'HOURLY', 'DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'];
  readonly weekDays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
  readonly weekdayOccurrences = [1, 2, 3, 4, 5];
  readonly months = [
    { value: 1, labelKey: 'admin.schedulers.months.JANUARY' },
    { value: 2, labelKey: 'admin.schedulers.months.FEBRUARY' },
    { value: 3, labelKey: 'admin.schedulers.months.MARCH' },
    { value: 4, labelKey: 'admin.schedulers.months.APRIL' },
    { value: 5, labelKey: 'admin.schedulers.months.MAY' },
    { value: 6, labelKey: 'admin.schedulers.months.JUNE' },
    { value: 7, labelKey: 'admin.schedulers.months.JULY' },
    { value: 8, labelKey: 'admin.schedulers.months.AUGUST' },
    { value: 9, labelKey: 'admin.schedulers.months.SEPTEMBER' },
    { value: 10, labelKey: 'admin.schedulers.months.OCTOBER' },
    { value: 11, labelKey: 'admin.schedulers.months.NOVEMBER' },
    { value: 12, labelKey: 'admin.schedulers.months.DECEMBER' }
  ];

  loading = false;
  schedulers: AdminManagedScheduler[] = [];
  drafts: Record<string, SchedulerDraft> = {};
  savingKeys = new Set<string>();
  runningKeys = new Set<string>();

  ngOnInit(): void {
    this.loadSchedulers();
  }

  loadSchedulers(): void {
    this.loading = true;
    this.api.listSchedulers().subscribe({
      next: (schedulers) => {
        this.schedulers = schedulers ?? [];
        this.drafts = Object.fromEntries(this.schedulers.map((scheduler) => [scheduler.key, this.toDraft(scheduler)]));
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.loading = false;
        this.handleError(error, 'admin.schedulers.loadError');
      }
    });
  }

  saveScheduler(scheduler: AdminManagedScheduler): void {
    const draft = this.drafts[scheduler.key];
    if (!draft || this.savingKeys.has(scheduler.key)) {
      return;
    }
    this.savingKeys.add(scheduler.key);
    this.api.updateScheduler(scheduler.key, this.toRequest(draft)).subscribe({
      next: (updated) => {
        this.schedulers = this.schedulers.map((item) => item.key === updated.key ? updated : item);
        this.drafts[updated.key] = this.toDraft(updated);
        this.savingKeys.delete(scheduler.key);
        this.toast.success(this.i18n.t('admin.schedulers.updateSuccess'));
      },
      error: (error: HttpErrorResponse) => {
        this.savingKeys.delete(scheduler.key);
        this.handleError(error, 'admin.schedulers.updateError');
      }
    });
  }

  runScheduler(schedulerKey: string): void {
    if (this.runningKeys.has(schedulerKey)) {
      return;
    }
    this.runningKeys.add(schedulerKey);
    this.api.runScheduler(schedulerKey).subscribe({
      next: (result) => {
        this.runningKeys.delete(schedulerKey);
        const message = result.message?.trim()
          ? result.message
          : this.i18n.t('admin.schedulers.runSuccess');
        this.toast.success(message);
        this.loadSchedulers();
      },
      error: (error: HttpErrorResponse) => {
        this.runningKeys.delete(schedulerKey);
        this.handleError(error, 'admin.schedulers.runError');
      }
    });
  }

  isDailyLike(frequency: AdminManagedSchedulerFrequency): boolean {
    return frequency === 'DAILY' || frequency === 'WEEKLY' || frequency === 'MONTHLY' || frequency === 'YEARLY';
  }

  isWeekly(frequency: AdminManagedSchedulerFrequency): boolean {
    return frequency === 'WEEKLY';
  }

  isMonthly(frequency: AdminManagedSchedulerFrequency): boolean {
    return frequency === 'MONTHLY';
  }

  isYearly(frequency: AdminManagedSchedulerFrequency): boolean {
    return frequency === 'YEARLY';
  }

  frequencyLabel(frequency: AdminManagedSchedulerFrequency): string {
    return `admin.schedulers.frequency.${frequency}`;
  }

  dayLabel(day: string): string {
    return `admin.schedulers.days.${day}`;
  }

  occurrenceLabel(occurrence: number): string {
    return `admin.schedulers.occurrence.${occurrence}`;
  }

  frequencyIntervalLabel(frequency: AdminManagedSchedulerFrequency): string {
    return `admin.schedulers.intervalLabel.${frequency}`;
  }

  showInterval(frequency: AdminManagedSchedulerFrequency): boolean {
    return frequency !== 'YEARLY';
  }

  showWeeklyFields(frequency: AdminManagedSchedulerFrequency): boolean {
    return frequency === 'WEEKLY';
  }

  showMonthlyFields(frequency: AdminManagedSchedulerFrequency): boolean {
    return frequency === 'MONTHLY';
  }

  showYearlyFields(frequency: AdminManagedSchedulerFrequency): boolean {
    return frequency === 'YEARLY';
  }

  showTimeField(frequency: AdminManagedSchedulerFrequency): boolean {
    return this.isDailyLike(frequency);
  }

  scheduleModeHint(frequency: AdminManagedSchedulerFrequency): string {
    switch (frequency) {
      case 'SECONDLY':
        return 'Declenche cette tache toutes les X secondes.';
      case 'MINUTELY':
        return 'Declenche cette tache toutes les X minutes.';
      case 'HOURLY':
        return 'Declenche cette tache toutes les X heures.';
      case 'DAILY':
        return 'Choisis un rythme en jours et une heure d execution.';
      case 'WEEKLY':
        return 'Choisis le jour de la semaine et l heure d execution.';
      case 'MONTHLY':
        return 'Choisis soit un jour fixe, soit une occurrence dans le mois.';
      case 'YEARLY':
        return 'Choisis soit une date precise, soit une occurrence dans un mois donne.';
      default:
        return '';
    }
  }

  enabledDescription(enabled: boolean): string {
    return enabled
      ? 'Le scheduler est actif et pourra se lancer automatiquement.'
      : 'Le scheduler est desactive et ne se lancera pas automatiquement.';
  }

  scheduleSummary(schedulerKey: string): string {
    const draft = this.drafts[schedulerKey];
    if (!draft) {
      return '';
    }
    const interval = Math.max(1, Math.trunc(draft.intervalValue || 1));
    const time = this.isDailyLike(draft.frequency) ? ` a ${draft.timeOfDay || '00:00'}` : '';

    if (draft.frequency === 'SECONDLY') {
      return interval === 1 ? 'Toutes les secondes' : `Toutes les ${interval} secondes`;
    }
    if (draft.frequency === 'MINUTELY') {
      return interval === 1 ? 'Toutes les minutes' : `Toutes les ${interval} minutes`;
    }
    if (draft.frequency === 'HOURLY') {
      return interval === 1 ? 'Toutes les heures' : `Toutes les ${interval} heures`;
    }
    if (draft.frequency === 'DAILY') {
      return interval === 1 ? `Tous les jours${time}` : `Tous les ${interval} jours${time}`;
    }
    if (draft.frequency === 'WEEKLY') {
      const day = this.i18n.t(this.dayLabel(draft.dayOfWeek || 'MONDAY')).toLowerCase();
      return interval === 1
        ? `Chaque ${day}${time}`
        : `Toutes les ${interval} semaines, le ${day}${time}`;
    }
    if (draft.frequency === 'MONTHLY') {
      if (draft.monthlyPattern === 'LAST_DAY') {
        return interval === 1
          ? `Le dernier jour du mois${time}`
          : `Tous les ${interval} mois, le dernier jour${time}`;
      }
      if (draft.monthlyPattern === 'NTH_WEEKDAY') {
        const occurrence = this.i18n.t(this.occurrenceLabel(draft.weekdayOccurrence || 1)).toLowerCase();
        const day = this.i18n.t(this.dayLabel(draft.dayOfWeek || 'MONDAY')).toLowerCase();
        return interval === 1
          ? `Le ${occurrence} ${day} du mois${time}`
          : `Tous les ${interval} mois, le ${occurrence} ${day}${time}`;
      }
      const dayOfMonth = Math.max(1, Math.min(31, Math.trunc(draft.dayOfMonth || 1)));
      return interval === 1
        ? `Le ${dayOfMonth} de chaque mois${time}`
        : `Tous les ${interval} mois, le ${dayOfMonth}${time}`;
    }

    const month = this.i18n.t(this.months.find((item) => item.value === (draft.monthOfYear || 1))?.labelKey || this.months[0].labelKey).toLowerCase();
    if (draft.yearlyPattern === 'NTH_WEEKDAY') {
      const occurrence = this.i18n.t(this.occurrenceLabel(draft.weekdayOccurrence || 1)).toLowerCase();
      const day = this.i18n.t(this.dayLabel(draft.dayOfWeek || 'MONDAY')).toLowerCase();
      return `Chaque annee, le ${occurrence} ${day} de ${month}${time}`;
    }
    const dayOfMonth = Math.max(1, Math.min(31, Math.trunc(draft.dayOfMonth || 1)));
    return `Chaque annee, le ${dayOfMonth} ${month}${time}`;
  }

  statusLabelKey(scheduler: AdminManagedScheduler): string {
    const rawStatus = (scheduler.lastStatus || '').trim().toUpperCase();
    if (!scheduler.enabled) {
      return 'admin.schedulers.status.DISABLED';
    }
    return `admin.schedulers.status.${rawStatus || 'IDLE'}`;
  }

  statusBadgeClass(scheduler: AdminManagedScheduler): string {
    const rawStatus = (scheduler.lastStatus || '').trim().toUpperCase();
    if (!scheduler.enabled) {
      return 'status-badge status-badge--disabled';
    }
    if (rawStatus === 'SUCCESS') {
      return 'status-badge status-badge--success';
    }
    if (rawStatus === 'ERROR') {
      return 'status-badge status-badge--error';
    }
    return 'status-badge status-badge--idle';
  }

  private toDraft(scheduler: AdminManagedScheduler): SchedulerDraft {
    const monthlyPattern: MonthlyPattern = scheduler.weekdayOccurrence
      ? 'NTH_WEEKDAY'
      : scheduler.lastDayOfMonth
        ? 'LAST_DAY'
        : 'DAY_OF_MONTH';
    const yearlyPattern: YearlyPattern = scheduler.weekdayOccurrence ? 'NTH_WEEKDAY' : 'DATE';
    return {
      enabled: scheduler.enabled,
      frequency: scheduler.frequency,
      intervalValue: scheduler.intervalValue,
      dayOfWeek: scheduler.dayOfWeek ?? null,
      weekdayOccurrence: scheduler.weekdayOccurrence ?? 1,
      dayOfMonth: scheduler.dayOfMonth ?? null,
      monthOfYear: scheduler.monthOfYear ?? 1,
      lastDayOfMonth: scheduler.lastDayOfMonth,
      timeOfDay: scheduler.timeOfDay ? scheduler.timeOfDay.slice(0, 5) : '00:00',
      monthlyPattern,
      yearlyPattern
    };
  }

  private toRequest(draft: SchedulerDraft): AdminManagedSchedulerUpdateRequest {
    const isMonthlyWeekday = this.isMonthly(draft.frequency) && draft.monthlyPattern === 'NTH_WEEKDAY';
    const isMonthlyLastDay = this.isMonthly(draft.frequency) && draft.monthlyPattern === 'LAST_DAY';
    const isYearlyWeekday = this.isYearly(draft.frequency) && draft.yearlyPattern === 'NTH_WEEKDAY';
    return {
      enabled: draft.enabled,
      frequency: draft.frequency,
      intervalValue: Math.max(1, Math.trunc(draft.intervalValue || 1)),
      dayOfWeek: this.isWeekly(draft.frequency) || isMonthlyWeekday || isYearlyWeekday ? draft.dayOfWeek : null,
      weekdayOccurrence: isMonthlyWeekday || isYearlyWeekday
        ? Math.max(1, Math.min(5, Math.trunc(draft.weekdayOccurrence || 1)))
        : null,
      dayOfMonth: (this.isMonthly(draft.frequency) && draft.monthlyPattern === 'DAY_OF_MONTH')
        || (this.isYearly(draft.frequency) && draft.yearlyPattern === 'DATE')
        ? Math.max(1, Math.min(31, Math.trunc(draft.dayOfMonth || 1)))
        : null,
      monthOfYear: this.isYearly(draft.frequency)
        ? Math.max(1, Math.min(12, Math.trunc(draft.monthOfYear || 1)))
        : null,
      lastDayOfMonth: isMonthlyLastDay,
      timeOfDay: this.isDailyLike(draft.frequency) ? (draft.timeOfDay || '00:00') : null
    };
  }

  private handleError(error: HttpErrorResponse, fallbackKey: string): void {
    const message = typeof error.error?.message === 'string' && error.error.message.trim()
      ? error.error.message.trim()
      : this.i18n.t(fallbackKey);
    this.toast.error(message);
  }
}
